import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { fetchData, getAgentById } from "../../api/client";
import { useChatbotSlots, useChatbotActions, useChatbotMutations } from "../../hooks/useChatbot";
import {
  auditChatbotFlow,
  applyChatbotFlowFixes,
  AUDIT_BATCH_FIX_LIMIT,
  AUDIT_SEVERITY_ERROR,
  AUDIT_SEVERITY_WARNING,
  AUDIT_SEVERITY_INFO,
  getAuditBatchPlan,
} from "../../utils/chatbotFlowAudit";
import { parseAuditEntityIndex } from "../../utils/chatbotFlowAuditRefs";
import { getUserFacingApiMessage } from "../../utils/getUserFacingApiMessage";
import {
  buildFullFlowDocument,
  downloadFlowJson,
  slotsToFlowDocument,
} from "../../utils/chatbotFlowExchange";
import { ensureAlterarSolicitacaoAction } from "../../utils/chatbotAlterarSolicitacaoAction";
import { fetchAutomationRuleForExport } from "./ChatbotFlowImportWizard";
import BulkActionConfirmStep from "../search/BulkActionConfirmStep";
import ChatbotFlowPathLinks from "./ChatbotFlowPathLinks";
import ChatbotFlowDialogSlotEditor, {
  ChatbotFlowDialogSplitLayout,
} from "./ChatbotFlowDialogSlotEditor";

const STEPS = { intro: 0, scanning: 1, review: 2, confirm: 3, done: 4 };

const EMPTY_AUDIT = {
  issues: [],
  summary: { total: 0, errors: 0, warnings: 0, info: 0, autoFixable: 0 },
};

function severityColor(severity) {
  if (severity === AUDIT_SEVERITY_ERROR) return "error";
  if (severity === AUDIT_SEVERITY_WARNING) return "warning";
  return "info";
}

function formatIssueRule(t, rule) {
  return t(`chatbotFlowAudit.rule_${rule}`, { defaultValue: rule });
}

function formatIssueSuggestion(t, issue) {
  const details = {
    ...issue.details,
    reasons: (issue.details?.reasons || []).join(", "),
    reasonLabels: issue.details?.reasonLabels || "",
  };
  if (details.refKind) {
    details.refKind = t(`chatbotFlowAudit.refKind_${details.refKind}`, { defaultValue: details.refKind });
  }
  if (!issue.autoFixable) {
    return t(`chatbotFlowAudit.review_${issue.rule}_${details.reason}`, {
      defaultValue: t(`chatbotFlowAudit.review_${issue.rule}`, {
        defaultValue: t("chatbotFlowAudit.suggestManual"),
        ...details,
      }),
      ...details,
    });
  }
  return t(`chatbotFlowAudit.fix_${issue.rule}_${details.reason}`, {
    defaultValue: t(`chatbotFlowAudit.fix_${issue.rule}`, {
      defaultValue: t("chatbotFlowAudit.suggestAuto"),
      ...details,
    }),
    ...details,
  });
}

export default function ChatbotFlowAuditDialog({
  userId,
  chatbotName,
  disabled,
  onError,
  onSuccess,
  onNavigateToSlot,
}) {
  const { t } = useTranslation("settings");
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(STEPS.intro);
  const [selected, setSelected] = useState(new Set());
  const [exportBusy, setExportBusy] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);
  const [applyBatchProgress, setApplyBatchProgress] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [auditResult, setAuditResult] = useState(EMPTY_AUDIT);
  const [dialogError, setDialogError] = useState(null);
  const [previewSlotId, setPreviewSlotId] = useState(null);

  const { data: slots = [], isLoading, refetch } = useChatbotSlots(userId);
  const { data: actionsCatalog = [] } = useChatbotActions(userId);
  const { createSlot, updateSlot, deleteSlot } = useChatbotMutations(userId);

  const alterarSolicitacaoForm = useMemo(() => {
    const catalog = ensureAlterarSolicitacaoAction(actionsCatalog);
    const action = catalog.find((a) => Number(a.tipo_acao) === 4 && Number(a.id_acao) === 4);
    return action?.parameterForm || null;
  }, [actionsCatalog]);

  const selectedIssues = useMemo(
    () => auditResult.issues.filter((i) => selected.has(i.id)),
    [auditResult.issues, selected]
  );

  const selectedBatchPlan = useMemo(
    () => getAuditBatchPlan(selectedIssues.length),
    [selectedIssues.length]
  );

  const safeFileName = (chatbotName || "chatbot-fluxo").replace(/[^-\w\s]+/g, "").trim() || "chatbot-fluxo";

  const buildExportLoaders = () => ({
    fetchAutomationRule: fetchAutomationRuleForExport,
    fetchUser: async (id) => {
      const res = await fetchData(`/users/${id}`, "GET");
      return res?.data ?? res;
    },
    fetchAgent: async (id) => getAgentById(id),
    fetchChannel: async (id) => {
      const res = await fetchData(`/channels/${id}`, "GET");
      return res?.data ?? res;
    },
    fetchType: async (id) => {
      const res = await fetchData(`/types/${id}`, "GET");
      return res?.data ?? res;
    },
    fetchSubtype: async (id) => {
      const res = await fetchData(`/types/${id}`, "GET");
      return res?.data ?? res;
    },
    fetchPaymentMethod: async (id) => {
      const res = await fetchData(`/payment-methods/${id}`, "GET");
      return res?.data ?? res;
    },
    fetchDeliveryMethod: async (id) => {
      const res = await fetchData(`/delivery-methods/${id}`, "GET");
      return res?.data ?? res;
    },
    fetchPosition: async (id) => {
      const res = await fetchData(`/positions/${id}`, "GET");
      return res?.data ?? res;
    },
  });

  const handleOpen = () => {
    setStep(STEPS.intro);
    setSelected(new Set());
    setApplyResult(null);
    setAuditResult(EMPTY_AUDIT);
    setDialogError(null);
    setPreviewSlotId(null);
    setOpen(true);
  };

  const handleClose = () => {
    if (applyBusy || scanBusy) return;
    setPreviewSlotId(null);
    setOpen(false);
  };

  const handleNavigateToSlot = (slotId) => {
    if (!slotId) return;
    setPreviewSlotId(Number(slotId));
    onNavigateToSlot?.(Number(slotId));
  };

  const handleRunScan = async () => {
    if (!slots.length) {
      setDialogError(t("chatbotConfigPage.flowExportEmpty"));
      return;
    }
    setDialogError(null);
    setScanBusy(true);
    setStep(STEPS.scanning);
    try {
      const fresh = await refetch();
      const slotsForAudit = Array.isArray(fresh?.data) && fresh.data.length ? fresh.data : slots;
      const entityRes = await fetchData("/chatbot/audit-entity-index", "GET");
      const entityIndex = parseAuditEntityIndex(entityRes?.data ?? entityRes);
      const result = auditChatbotFlow({
        slots: slotsForAudit,
        actionsCatalog,
        entityIndex,
        parameterForm: alterarSolicitacaoForm,
      });
      setAuditResult(result);
      const autoIds = result.issues.filter((i) => i.autoFixable).map((i) => i.id);
      setSelected(new Set(autoIds));
      setStep(STEPS.review);
    } catch (err) {
      setStep(STEPS.intro);
      setDialogError(getUserFacingApiMessage(err).message || t("chatbotFlowAudit.scanError"));
    } finally {
      setScanBusy(false);
    }
  };

  const toggleIssue = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleExportBackup = async () => {
    if (!slots.length) {
      onError?.(t("chatbotConfigPage.flowExportEmpty"));
      return;
    }
    setExportBusy(true);
    try {
      const doc = await buildFullFlowDocument(slots, {
        chatbotName,
        sourceUserId: userId,
        parameterForm: alterarSolicitacaoForm,
        loaders: buildExportLoaders(),
      });
      downloadFlowJson(doc, `${safeFileName}-backup-audit`);
      onSuccess?.(t("chatbotFlowAudit.exportSuccess"));
    } catch {
      downloadFlowJson(
        slotsToFlowDocument(slots, { chatbotName, sourceUserId: userId }),
        `${safeFileName}-backup-audit`
      );
      onSuccess?.(t("chatbotFlowAudit.exportSuccess"));
    } finally {
      setExportBusy(false);
    }
  };

  const handleApply = async () => {
    setApplyBusy(true);
    setApplyBatchProgress(null);
    setDialogError(null);
    try {
      const result = await applyChatbotFlowFixes(selectedIssues, {
        createSlot,
        updateSlot,
        deleteSlot,
        userId,
        onBatchProgress: setApplyBatchProgress,
        applyBatchFn: async (fixable) => {
          const res = await fetchData("/chatbot/slots/audit/apply", "POST", {
            user_id: userId,
            fixes: fixable.map((issue) => ({
              issueId: issue.id,
              kind: issue.fix.kind,
              payload: issue.fix.payload,
            })),
          });
          const data = res?.data ?? res ?? {};
          return {
            applied: Array.isArray(data.applied) ? data.applied : [],
            failed: Array.isArray(data.failed) ? data.failed : [],
            batch: true,
          };
        },
      });
      await refetch();
      await queryClient.invalidateQueries(["chatbot"]);
      setApplyResult(result);
      setStep(STEPS.done);
      if (result.partial && result.batchErrors?.length) {
        setDialogError(
          t("chatbotFlowAudit.applyPartialError", {
            applied: result.applied.length,
            batch: result.batchErrors[0].batch,
            message: result.batchErrors[0].message,
          })
        );
      }
      onSuccess?.(
        t("chatbotFlowAudit.applySuccess", {
          applied: result.applied.length,
          failed: result.failed.length,
        })
      );
    } catch (err) {
      setStep(STEPS.review);
      setDialogError(getUserFacingApiMessage(err).message || t("chatbotFlowAudit.applyError"));
    } finally {
      setApplyBusy(false);
      setApplyBatchProgress(null);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<FactCheckOutlined />}
        disabled={disabled || !userId}
        onClick={handleOpen}
        sx={{ textTransform: "none" }}
      >
        {t("chatbotFlowAudit.openButton")}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={previewSlotId && step === STEPS.review ? "xl" : "md"}
        fullWidth
      >
        <DialogTitle>{t("chatbotFlowAudit.title")}</DialogTitle>

        {step === STEPS.confirm ? (
          <BulkActionConfirmStep
            impactCount={selectedIssues.length}
            entitySingular={t("chatbotFlowAudit.correctionSingular")}
            entityPlural={t("chatbotFlowAudit.correctionPlural")}
            impactVerb={t("chatbotFlowAudit.impactVerbApply")}
            irreversible
            actionTitle={t("chatbotFlowAudit.confirmApplyTitle")}
            actionDescription={
              selectedBatchPlan.batches > 1
                ? t("chatbotFlowAudit.confirmApplyDescriptionBatches", {
                    count: selectedIssues.length,
                    batches: selectedBatchPlan.batches,
                    limit: AUDIT_BATCH_FIX_LIMIT,
                  })
                : t("chatbotFlowAudit.confirmApplyDescription")
            }
            warnings={
              selectedBatchPlan.batches > 1
                ? [
                    t("chatbotFlowAudit.batchApplyInfo", {
                      count: selectedIssues.length,
                      batches: selectedBatchPlan.batches,
                      limit: AUDIT_BATCH_FIX_LIMIT,
                    }),
                  ]
                : []
            }
            confirmCheckboxLabel={t("chatbotFlowAudit.confirmCheckbox")}
            confirmButtonLabel={
              applyBusy && applyBatchProgress
                ? t("chatbotFlowAudit.applyingBatch", {
                    batch: applyBatchProgress.batch,
                    total: applyBatchProgress.totalBatches,
                  })
                : t("chatbotFlowAudit.applySelected", { count: selectedIssues.length })
            }
            onConfirm={handleApply}
            onBack={() => setStep(STEPS.review)}
            submitting={applyBusy}
          />
        ) : (
          <>
        <DialogContent
          dividers
          sx={
            step === STEPS.review
              ? {
                  p: 0,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: previewSlotId ? 520 : undefined,
                }
              : undefined
          }
        >
          {dialogError && (
            <Alert severity="error" onClose={() => setDialogError(null)} sx={{ m: 2, mb: step === STEPS.review ? 0 : 2 }}>
              {dialogError}
            </Alert>
          )}

          {step === STEPS.intro && (
            <Box sx={{ px: 3, pb: 3 }}>
            <Stack spacing={2}>
              <Alert severity="info">{t("chatbotFlowAudit.scanOnlyHint")}</Alert>
              <Alert severity="warning">{t("chatbotFlowAudit.irreversibleWarning")}</Alert>
              <Typography variant="body2">{t("chatbotFlowAudit.introBody")}</Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={exportBusy ? <CircularProgress size={16} /> : <FileDownloadOutlined />}
                onClick={handleExportBackup}
                disabled={exportBusy || isLoading || !slots.length}
                sx={{ alignSelf: "flex-start", textTransform: "none" }}
              >
                {t("chatbotFlowAudit.exportBackupButton")}
              </Button>
            </Stack>
            </Box>
          )}

          {step === STEPS.scanning && (
            <Box sx={{ px: 3, pb: 3 }}>
            <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                {t("chatbotFlowAudit.scanning")}
              </Typography>
            </Stack>
            </Box>
          )}

          {step === STEPS.review && (
            <ChatbotFlowDialogSplitLayout
              slotEditorOpen={!!previewSlotId}
              slotEditor={
                <ChatbotFlowDialogSlotEditor
                  userId={userId}
                  slotId={previewSlotId}
                  slots={slots}
                  onClose={() => setPreviewSlotId(null)}
                  onError={(msg) => msg && setDialogError(msg)}
                />
              }
            >
            <Stack spacing={2} sx={{ p: 3 }}>
              <Alert severity="warning">{t("chatbotFlowAudit.irreversibleWarning")}</Alert>
              {selectedBatchPlan.batches > 1 && (
                <Alert severity="info">
                  {t("chatbotFlowAudit.batchApplyInfo", {
                    count: selectedIssues.length,
                    batches: selectedBatchPlan.batches,
                    limit: AUDIT_BATCH_FIX_LIMIT,
                  })}
                </Alert>
              )}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={t("chatbotFlowAudit.summaryTotal", { count: auditResult.summary.total })} size="small" />
                <Chip
                  label={t("chatbotFlowAudit.summaryErrors", { count: auditResult.summary.errors })}
                  size="small"
                  color="error"
                  variant="outlined"
                />
                <Chip
                  label={t("chatbotFlowAudit.summaryWarnings", { count: auditResult.summary.warnings })}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
                <Chip
                  label={t("chatbotFlowAudit.summaryAutoFix", { count: auditResult.summary.autoFixable })}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={exportBusy ? <CircularProgress size={16} /> : <FileDownloadOutlined />}
                  onClick={handleExportBackup}
                  disabled={exportBusy || !slots.length}
                  sx={{ textTransform: "none" }}
                >
                  {t("chatbotFlowAudit.exportBackupButton")}
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() =>
                    setSelected(new Set(auditResult.issues.filter((i) => i.autoFixable).map((i) => i.id)))
                  }
                  sx={{ textTransform: "none" }}
                >
                  {t("chatbotFlowAudit.selectAllAuto")}
                </Button>
                <Button variant="text" size="small" onClick={() => setSelected(new Set())} sx={{ textTransform: "none" }}>
                  {t("chatbotFlowAudit.selectNone")}
                </Button>
              </Stack>

              {auditResult.issues.length === 0 ? (
                <Alert severity="success">{t("chatbotFlowAudit.noIssues")}</Alert>
              ) : (
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell>{t("chatbotFlowAudit.colSeverity")}</TableCell>
                        <TableCell>{t("chatbotFlowAudit.colStep")}</TableCell>
                        <TableCell>{t("chatbotFlowAudit.colIssue")}</TableCell>
                        <TableCell>{t("chatbotFlowAudit.colSuggestion")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {auditResult.issues.map((issue) => (
                        <TableRow key={issue.id} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selected.has(issue.id)}
                              disabled={!issue.autoFixable}
                              onChange={() => toggleIssue(issue.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={t(`chatbotFlowAudit.severity_${issue.severity}`)}
                              color={severityColor(issue.severity)}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <ChatbotFlowPathLinks
                              path={issue.slotPath}
                              slots={slots}
                              targetSlotId={issue.slotId}
                              onNavigate={handleNavigateToSlot}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {formatIssueRule(t, issue.rule)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatIssueSuggestion(t, issue)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Stack>
            </ChatbotFlowDialogSplitLayout>
          )}

          {step === STEPS.done && (
            <Box sx={{ px: 3, pb: 3 }}>
            <Stack spacing={2}>
              <Alert severity={applyResult?.failed?.length ? "warning" : "success"}>
                {t("chatbotFlowAudit.applyDone", {
                  applied: applyResult?.applied?.length ?? 0,
                  failed: applyResult?.failed?.length ?? 0,
                })}
              </Alert>
              {applyResult?.failed?.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {applyResult.failed.map((f) => f.message).join("; ")}
                </Typography>
              )}
            </Stack>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {step === STEPS.intro && (
            <>
              <Button onClick={handleClose} sx={{ textTransform: "none" }}>
                {t("chatbotFlowAudit.close")}
              </Button>
              <Button
                variant="contained"
                onClick={handleRunScan}
                disabled={isLoading || scanBusy || !slots.length}
                sx={{ textTransform: "none" }}
              >
                {scanBusy ? <CircularProgress size={20} /> : t("chatbotFlowAudit.startScan")}
              </Button>
            </>
          )}
          {step === STEPS.review && (
            <>
              <Button onClick={handleClose} sx={{ textTransform: "none" }}>
                {t("chatbotFlowAudit.close")}
              </Button>
              <Button
                variant="contained"
                disabled={selectedIssues.length === 0}
                onClick={() => setStep(STEPS.confirm)}
                sx={{ textTransform: "none" }}
              >
                {t("chatbotFlowAudit.applySelected", { count: selectedIssues.length })}
              </Button>
            </>
          )}
          {step === STEPS.done && (
            <Button variant="contained" onClick={handleClose} sx={{ textTransform: "none" }}>
              {t("chatbotFlowAudit.close")}
            </Button>
          )}
        </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
